import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Star, MessageCircle, Flag, Loader2, CheckCircle, Calendar } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { ServiceCard } from '../components/common/ServiceCard';
import { RatingStars } from '../components/common/RatingStars';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ReportModal from '../components/common/ReportModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function UserProfilePage() {
  const { id } = useParams();
  const { user: currentUser, isAuthenticated, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, servicesRes, reviewsRes] = await Promise.all([
        axios.get(`${API_URL}/users/${id}`),
        axios.get(`${API_URL}/services?limit=50`),
        axios.get(`${API_URL}/reviews/user/${id}`),
      ]);
      setProfile(profileRes.data);
      setServices(servicesRes.data.filter((s) => s.provider_id === id && s.active));
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Utilisateur non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return;

    try {
      await api.post('/reports', {
        reported_user_id: id,
        reason: reportReason,
      });
      toast.success('Signalement envoyé');
      setReportModalOpen(false);
      setReportReason('');
    } catch (error) {
      toast.error('Erreur lors du signalement');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!profile) return null;

  const isOwnProfile = currentUser?.id === profile.id;

  return (
    <Layout>
      <div className="container-app py-8 md:py-12">
        {/* Profile Header */}
        <Card className="rounded-2xl overflow-hidden mb-8">
          <div className="h-32 bg-gradient-to-r from-primary/20 to-emerald-500/20" />
          <CardContent className="relative p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <Avatar className="w-24 h-24 -mt-16 ring-4 ring-background">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="bg-primary text-primary-foreground text-3xl">
                  {profile.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold">{profile.full_name}</h1>
                  {profile.verified && (
                    <CheckCircle className="w-6 h-6 text-primary" />
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-4">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {profile.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Membre depuis {format(new Date(profile.created_at), 'MMMM yyyy', { locale: fr })}
                  </span>
                </div>

                {profile.bio && (
                  <p className="text-muted-foreground mb-4">{profile.bio}</p>
                )}

                <div className="flex flex-wrap gap-4">
                  <Badge variant="secondary" className="text-base py-1">
                    {profile.total_exchanges} échanges
                  </Badge>
                  {profile.rating_count > 0 && (
                    <div className="flex items-center gap-2">
                      <RatingStars rating={profile.rating_average} count={profile.rating_count} />
                    </div>
                  )}
                </div>
              </div>

              {!isOwnProfile && isAuthenticated && (
                <div className="flex gap-2">
                  <Link to={`/messages?user=${profile.id}`}>
                    <Button className="btn-primary" data-testid="contact-user-btn">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contacter
                    </Button>
                  </Link>
                  <Button variant="ghost" className="text-muted-foreground" onClick={() => setReportModalOpen(true)}>
                    <Flag className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        {services.length > 0 && (
          <Card className="rounded-2xl mb-8">
            <CardHeader>
              <CardTitle>Services proposés ({services.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>Avis ({reviews.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-border pb-4 last:border-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={review.reviewer?.avatar_url} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {review.reviewer?.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{review.reviewer?.full_name}</span>
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(review.created_at), 'dd MMM yyyy', { locale: fr })}
                          </span>
                        </div>
                        <RatingStars rating={review.rating} showCount={false} size="sm" />
                        {review.comment && (
                          <p className="text-muted-foreground mt-2">{review.comment}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Aucun avis pour le moment
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Report Modal */}
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        type="user"
        targetId={id}
        api={api}
      />
    </Layout>
  );
}
